import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import menuApi from 'api/requests/menu';
import establishmentsApi from 'api/requests/establishments';
import PublicProductSheet from './PublicProductSheet';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

function formatPrice(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function PublicMenuPage() {
    const { slug } = useParams();
    const [menu, setMenu] = useState([]);
    const [establishment, setEstablishment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeCategory, setActiveCategory] = useState('all');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const pillsRef = useRef(null);
    const activePillRef = useRef(null);

    useEffect(() => {
        Promise.all([
            menuApi.getPublicMenu(slug),
            establishmentsApi.getBySlug(slug),
        ])
            .then(([menuRes, estRes]) => {
                const items = menuRes.data.data || [];
                setMenu(items);
                setEstablishment(estRes.data.data || null);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [slug]);

    const nonEmptyMenu = useMemo(() => menu.filter(c => c.products?.length > 0), [menu]);

    const visibleCategories = useMemo(() => {
        if (activeCategory === 'all') return nonEmptyMenu;
        return nonEmptyMenu.filter(c => c.id === activeCategory);
    }, [nonEmptyMenu, activeCategory]);

    function selectCategory(id) {
        setActiveCategory(id);
        setTimeout(() => {
            activePillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 50);
    }

    if (loading) return (
        <PageWrapper>
            <LoadingCenter>
                <SpinnerRing>
                    <SpinnerDot />
                </SpinnerRing>
            </LoadingCenter>
        </PageWrapper>
    );

    if (error) return (
        <PageWrapper>
            <ErrorState>
                <ErrorIconWrap>
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="23" stroke={colors.nightLight} strokeWidth="2" strokeDasharray="4 3"/>
                        <path d="M16 28c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={colors.nightLight} strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="18" cy="20" r="2" fill={colors.nightLight}/>
                        <circle cx="30" cy="20" r="2" fill={colors.nightLight}/>
                    </svg>
                </ErrorIconWrap>
                <ErrorTitle>Cardápio não encontrado</ErrorTitle>
                <ErrorHint>Verifique o link ou entre em contato com o estabelecimento.</ErrorHint>
            </ErrorState>
        </PageWrapper>
    );

    return (
        <PageWrapper>
            {/* Establishment Header */}
            <HeaderSection>
                <HeaderOverlay />
                {establishment?.backgroundImage?.path && (
                    <HeaderBg src={establishment.backgroundImage.path} alt="" />
                )}
                <HeaderContent>
                    {establishment?.profileImage?.path ? (
                        <LogoImage
                            src={establishment.profileImage.path}
                            alt={establishment?.name || ''}
                            onError={e => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                        />
                    ) : (
                        <LogoFallback>
                            {(establishment?.name || slug || '?').charAt(0).toUpperCase()}
                        </LogoFallback>
                    )}
                    <HeaderInfo>
                        <EstablishmentName>{establishment?.name || slug}</EstablishmentName>
                        {establishment?.tagsEstablishments?.length > 0 && (
                            <TagList>
                                {establishment.tagsEstablishments.map((tag, i) => (
                                    <React.Fragment key={tag.id || i}>
                                        {i > 0 && <TagDot />}
                                        <TagItem>{tag.name}</TagItem>
                                    </React.Fragment>
                                ))}
                            </TagList>
                        )}
                    </HeaderInfo>
                </HeaderContent>
            </HeaderSection>

            {/* Category Navigation */}
            <StickyBar>
                <PillsTrack ref={pillsRef}>
                    <CategoryPill
                        ref={activeCategory === 'all' ? activePillRef : null}
                        $active={activeCategory === 'all'}
                        onClick={() => selectCategory('all')}
                    >
                        Todos
                    </CategoryPill>
                    {nonEmptyMenu.map(cat => (
                        <CategoryPill
                            key={cat.id}
                            ref={activeCategory === cat.id ? activePillRef : null}
                            $active={activeCategory === cat.id}
                            onClick={() => selectCategory(cat.id)}
                        >
                            {cat.name}
                            {activeCategory === cat.id && <PillCount>{cat.products?.length || 0}</PillCount>}
                        </CategoryPill>
                    ))}
                </PillsTrack>
            </StickyBar>

            <Content>
                {visibleCategories.map(cat => (
                    <CategoryBlock key={cat.id}>
                        <CategoryHeader>
                            <CategoryName>{cat.name}</CategoryName>
                            {cat.description && (
                                <CategoryDesc>{cat.description}</CategoryDesc>
                            )}
                            <CategoryDivider />
                        </CategoryHeader>

                        <ProductGrid>
                            {cat.products.map((product, idx) => (
                                <ProductCard
                                    key={product.id}
                                    $delay={idx}
                                    onClick={() => setSelectedProduct(product)}
                                >
                                    {product.image ? (
                                        <ProductImageWrap>
                                            <ProductImage
                                                src={product.image.thumb}
                                                alt={product.name}
                                                onError={e => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                                            />
                                        </ProductImageWrap>
                                    ) : (
                                        <ProductImageWrap>
                                            <ProductImagePlaceholder>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 3c1.66 0 3 1.34 3 3v2H9V6c0-1.66 1.34-3 3-3z" fill="rgba(255,255,255,0.3)"/>
                                                </svg>
                                            </ProductImagePlaceholder>
                                        </ProductImageWrap>
                                    )}
                                    <ProductInfo>
                                        <ProductName>{product.name}</ProductName>
                                        {product.description && (
                                            <ProductDesc>{product.description}</ProductDesc>
                                        )}
                                        <ProductFooter>
                                            <ProductPrice>{formatPrice(product.base_price)}</ProductPrice>
                                        </ProductFooter>
                                    </ProductInfo>
                                </ProductCard>
                            ))}
                        </ProductGrid>
                    </CategoryBlock>
                ))}
            </Content>

            {selectedProduct && (
                <PublicProductSheet
                    product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                />
            )}
        </PageWrapper>
    );
}

/* ── Animations ── */

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const fadeSlideUp = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(105, 12, 228, 0.25); }
    50% { box-shadow: 0 0 0 8px rgba(105, 12, 228, 0); }
`;

/* ── Layout ── */

const PageWrapper = styled.div`
    min-height: 100vh;
    background: #fafafa;
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const LoadingCenter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
`;

const SpinnerRing = styled.div`
    width: 44px;
    height: 44px;
    border: 2.5px solid #e8e8ec;
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.8s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
    position: relative;
`;

const SpinnerDot = styled.div`
    position: absolute;
    top: -3px;
    left: 50%;
    transform: translateX(-50%);
    width: 6px;
    height: 6px;
    background: ${colors.occasionalPurple};
    border-radius: 50%;
`;

/* ── Error ── */

const ErrorState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 80vh;
    text-align: center;
    padding: 24px;
    animation: ${fadeSlideUp} 0.5s ease-out;
`;

const ErrorIconWrap = styled.div`
    margin-bottom: 20px;
    opacity: 0.6;
`;

const ErrorTitle = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 8px;
    letter-spacing: -0.3px;
`;

const ErrorHint = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0;
    max-width: 280px;
    line-height: 1.5;
`;

/* ── Header ── */

const HeaderSection = styled.div`
    position: relative;
    background: ${colors.nightDark};
    overflow: hidden;
    padding: 48px 20px 28px;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 56px 20px 32px;
    }
`;

const HeaderBg = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(2px) brightness(0.35);
`;

const HeaderOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(
        to bottom,
        rgba(26, 26, 26, 0.6) 0%,
        rgba(26, 26, 26, 0.85) 100%
    );
    z-index: 1;
`;

const HeaderContent = styled.div`
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: 860px;
    margin: 0 auto;
    animation: ${fadeSlideUp} 0.6s ease-out;
`;

const LogoImage = styled.img`
    width: 68px;
    height: 68px;
    border-radius: 16px;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.15);
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 80px;
        height: 80px;
    }
`;

const LogoFallback = styled.div`
    width: 68px;
    height: 68px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: 600;
    color: #fff;
    flex-shrink: 0;
`;

const HeaderInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const EstablishmentName = styled.h1`
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    margin: 0;
    letter-spacing: -0.4px;
    line-height: 1.2;

    @media screen and (min-width: ${breakpoints.md}) {
        font-size: 26px;
    }
`;

const TagList = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const TagItem = styled.span`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    letter-spacing: 0.2px;
`;

const TagDot = styled.span`
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.35);
    flex-shrink: 0;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ $open }) => $open ? '#34d399' : 'rgba(255,255,255,0.5)'};
    margin-top: 2px;
`;

const StatusDot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $open }) => $open ? '#34d399' : 'rgba(255,255,255,0.3)'};
    ${({ $open }) => $open && css`
        animation: ${pulseGlow} 2s ease-in-out infinite;
        box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.3);
    `}
`;

/* ── Sticky Category Bar ── */

const StickyBar = styled.div`
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    border-bottom: 1px solid #eee;
    box-shadow: 0 1px 12px rgba(0, 0, 0, 0.04);
`;

const PillsTrack = styled.div`
    display: flex;
    gap: 6px;
    padding: 12px 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 900px;
    margin: 0 auto;

    &::-webkit-scrollbar { display: none; }
    scrollbar-width: none;
`;

const PillCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.25);
    font-size: 11px;
    font-weight: 600;
    margin-left: 6px;
    padding: 0 4px;
`;

const CategoryPill = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 8px 18px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? '600' : '500'};
    letter-spacing: -0.1px;
    cursor: pointer;
    border: none;
    background: ${({ $active }) => $active ? colors.occasionalPurple : '#f3f3f5'};
    color: ${({ $active }) => $active ? '#fff' : colors.night};
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: ${({ $active }) => $active ? colors.occasionalPurple : '#eae9ed'};
    }
`;

/* ── Content ── */

const Content = styled.div`
    padding: 20px 16px 40px;
    display: flex;
    flex-direction: column;
    gap: 48px;

    @media screen and (min-width: ${breakpoints.lg}) {
        max-width: 900px;
        margin: 0 auto;
        padding: 28px 20px 60px;
        gap: 56px;
    }
`;

const CategoryBlock = styled.div`
    animation: ${fadeSlideUp} 0.35s ease-out;
`;

const CategoryHeader = styled.div`
    margin-bottom: 20px;
`;

const CategoryName = styled.h2`
    font-size: 22px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 4px;
    letter-spacing: -0.4px;
`;

const CategoryDesc = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0 0 12px;
    line-height: 1.5;
`;

const CategoryDivider = styled.div`
    width: 32px;
    height: 3px;
    border-radius: 2px;
    background: ${colors.occasionalPurple};
    margin-top: 10px;
    opacity: 0.6;
`;

/* ── Product Grid ── */

const ProductGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 14px;
    }
`;

const ProductCard = styled.div`
    display: flex;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid #f0f0f2;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s ease, transform 0.15s ease;
    animation: ${fadeSlideUp} 0.4s ease-out;
    animation-delay: ${({ $delay }) => Math.min($delay * 40, 300)}ms;
    animation-fill-mode: both;

    &:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        transform: translateY(-1px);
    }
    &:active {
        transform: scale(0.985);
    }
`;

const ProductImageWrap = styled.div`
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 110px;
        height: 110px;
    }
`;

const ProductImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${ProductCard}:hover & {
        transform: scale(1.05);
    }
`;

const ProductImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #e8e8ec 0%, #d8d8de 100%);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ProductInfo = styled.div`
    flex: 1;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const ProductName = styled.span`
    font-weight: 600;
    font-size: 15px;
    color: ${colors.nightDark};
    letter-spacing: -0.2px;
    line-height: 1.3;
`;

const ProductDesc = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.45;
    margin-top: 4px;
`;

const ProductFooter = styled.div`
    margin-top: auto;
    padding-top: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ProductPrice = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    letter-spacing: -0.3px;
`;
